/**
 * PostgreSQL JSON-path extraction — extract-expression parser and the
 * string/identifier scan helpers it depends on.
 *
 * Split out of `extractJsonPathFromCursor.pgsql.ts` (§9.2 Priority-5 —
 * "MERGE-2 extractor file size") once that file crossed the ~200-line soft
 * guide (it was 311 lines). Pure mechanical move — no behavior change; see
 * the entry file's docblock for the supported/unsupported PG JSON path forms
 * these parsers implement.
 */

import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';

// ─────────────────────────────────────────────────────────────────────────────
// Extract-expression parser
// ─────────────────────────────────────────────────────────────────────────────

type ExtractResult = { column: string; canonicalPath: string };

/**
 * Parse a PG JSON text-extract expression whose closing `'` sits at `end`.
 * Supports `->>` single-hop, chained `->'a'->>'b'`, `->N->>'key'`, and
 * `#>>'{…}'` forms. Returns `null` when the pattern isn't recognized.
 */
export function parseExtractExprAt(text: string, end: number): ExtractResult | null {
  // Read the string literal at `end` — this is the last path argument.
  const lastLit = readQuotedStringEndingAt(text, end);
  if (!lastLit) return null;

  // Identify the operator immediately before the opening quote.
  let j = lastLit.openIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;

  const threeBack = text.slice(Math.max(0, j - 2), j + 1);
  const twoBack = text.slice(Math.max(0, j - 1), j + 1);

  if (threeBack.endsWith('#>>')) {
    // col #>> '{a,b,c}' form — op spans j-2..j, so char before op is at j-3.
    const beforeOp = j - 3;
    const canonicalPath = parsePgArrayLiteral(lastLit.content);
    if (!canonicalPath) return null;
    const col = readBareIdentBefore(text, beforeOp);
    if (!col) return null;
    return { column: col, canonicalPath };
  }

  if (threeBack.endsWith('->>')) {
    // col->>'key' chain (possibly with preceding -> hops).
    // Op spans j-2..j, so char before op is at j-3.
    const beforeOp = j - 3;
    return parseDashChainEndingWith(text, beforeOp, lastLit.content);
  }

  // `->>` could also look like `->` + `>` — but we have `threeBack` check above.
  // Check for `->` (json-typed extract, not text) — not useful for enum lookup.
  if (twoBack === '->') return null;

  return null;
}

/**
 * Parse the `->>` chain starting from `beforeLastOp` (the index just before
 * the `->>` operator for the last argument). Walks back collecting preceding
 * `->'key'` / `->N` hops. `lastArgRaw` is the raw string content of the
 * final `->>` argument.
 */
function parseDashChainEndingWith(
  text: string,
  beforeLastOp: number,
  lastArgRaw: string,
): ExtractResult | null {
  // The last segment from the `->>` argument. If it's a canonical JSONPath
  // (`$.…`), the whole path is already in it — no preceding hops needed.
  const lastArgTrimmed = lastArgRaw.trim();

  if (lastArgTrimmed.startsWith('$')) {
    // User-typed canonical form: `col->>'$.a.b'` — treat as full path.
    const col = readBareIdentBefore(text, beforeLastOp);
    if (!col) return null;
    return { column: col, canonicalPath: normalizeJsonPath(lastArgTrimmed) };
  }

  // Plain key: build the chain.
  const segments: string[] = [lastArgTrimmed];

  let j = beforeLastOp;
  // Walk back through zero or more preceding `->` hops.
  for (;;) {
    while (j >= 0 && /\s/.test(text[j]!)) j--;
    if (j < 0) break;

    const ch = text[j]!;

    if (ch === "'") {
      // Quoted key: `col->'a'`
      const lit = readQuotedStringEndingAt(text, j);
      if (!lit) break;
      // Expect `->` before this argument.
      let k = lit.openIdx - 1;
      while (k >= 0 && /\s/.test(text[k]!)) k--;
      const twoC = text.slice(Math.max(0, k - 1), k + 1);
      if (twoC !== '->') break;
      k -= 2; // move before the `->`
      segments.unshift(lit.content.trim());
      j = k;
    } else if (/\d/.test(ch)) {
      // Numeric index: `->2`
      const numEnd = j + 1;
      while (j >= 0 && /\d/.test(text[j]!)) j--;
      const numStr = text.slice(j + 1, numEnd);
      const idx = parseInt(numStr, 10);
      if (Number.isNaN(idx)) break;
      // Expect `->` before the number.
      let k = j;
      while (k >= 0 && /\s/.test(text[k]!)) k--;
      const twoC = text.slice(Math.max(0, k - 1), k + 1);
      if (twoC !== '->') break;
      k -= 2;
      segments.unshift(`[${idx}]`);
      j = k;
    } else {
      break;
    }
  }

  const col = readBareIdentBefore(text, j);
  if (!col) return null;

  // Build the canonical path from segments.
  // Segments: plain string keys (`.key`) or `[N]` (array index).
  let path = '$';
  for (const seg of segments) {
    if (seg.startsWith('[')) {
      path += seg;
    } else {
      path += `.${seg}`;
    }
  }
  return { column: col, canonicalPath: normalizeJsonPath(path) };
}

// ─────────────────────────────────────────────────────────────────────────────
// String literal + identifier helpers
// ─────────────────────────────────────────────────────────────────────────────

type QuotedString = {
  content: string;
  /** Index of the opening `'`. */
  openIdx: number;
};

function readQuotedStringEndingAt(text: string, end: number): QuotedString | null {
  if (text[end] !== "'") return null;
  let i = end - 1;
  let content = '';
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        content = "'" + content;
        i -= 2;
        continue;
      }
      return { content, openIdx: i };
    }
    if (ch === '\n') return null;
    content = ch + content;
    i--;
  }
  return null;
}

function readBareIdentBefore(text: string, end: number): string | null {
  let j = end;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;
  const idEnd = j + 1;
  // Accept double-quoted identifiers ("metadata") in addition to bare ones.
  while (j >= 0 && /[A-Za-z0-9_"]/.test(text[j]!)) j--;
  if (j + 1 === idEnd) return null;
  const id = text.slice(j + 1, idEnd).replace(/^"|"$/g, '');
  if (id.length === 0 || /^\d/.test(id)) return null; // pure digit run = not an identifier
  return id;
}

/**
 * Parse the content of a PG path-array literal `{a,b,c}` into a canonical
 * JSONPath. Integer elements become array indices `[N]`.
 *
 * `'{a,b}'`      → `$.a.b`
 * `'{items,0,name}'` → `$.items[0].name`
 * `'{}'`         → `$`
 */
export function parsePgArrayLiteral(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  const inner = trimmed.slice(1, -1).trim();
  if (inner === '') return '$';
  const parts = inner.split(',').map((s) => s.trim());
  let path = '$';
  for (const part of parts) {
    if (part === '') return null;
    if (/^\d+$/.test(part)) {
      path += `[${part}]`;
    } else {
      path += `.${part}`;
    }
  }
  return normalizeJsonPath(path);
}
